import { HStack, Icon, IconButton, Tooltip } from '@chakra-ui/react';
import { TbEdit, TbTrash } from 'react-icons/tb';

export interface SettingsRowActionsProps {
    /** Opens the row's editor. Omit to hide the edit button. */
    onEdit?: () => void;
    /** Deletes/removes the row. Omit to hide the delete button. */
    onDelete?: () => void;
    /** Tooltip and aria-label for the edit button. */
    editLabel?: string;
    /** Tooltip and aria-label for the delete button. */
    deleteLabel?: string;
    isEditDisabled?: boolean;
    isDeleteDisabled?: boolean;
}

/**
 * Row-level edit and delete buttons for settings tables. Replaces the kebab
 * menu that each table used to render: the two actions are common enough that
 * hiding them behind a popover only costs a click.
 */
const SettingsRowActions = ({
    onEdit,
    onDelete,
    editLabel = 'Edit',
    deleteLabel = 'Delete',
    isEditDisabled = false,
    isDeleteDisabled = false,
}: SettingsRowActionsProps) => (
    <HStack spacing={1} justify="center">
        {onEdit && (
            <Tooltip label={editLabel} openDelay={400} hasArrow>
                <IconButton
                    aria-label={editLabel}
                    icon={<Icon as={TbEdit as React.ElementType} boxSize={4} />}
                    onClick={onEdit}
                    isDisabled={isEditDisabled}
                    size="xs"
                    variant="ghost"
                    color="gray.600"
                    _hover={{ bg: 'gray.100', color: 'gray.800' }}
                    _active={{ bg: 'gray.200' }}
                    _focus={{ boxShadow: 'none' }}
                />
            </Tooltip>
        )}
        {onDelete && (
            <Tooltip label={deleteLabel} openDelay={400} hasArrow>
                <IconButton
                    aria-label={deleteLabel}
                    icon={<Icon as={TbTrash as React.ElementType} boxSize={4} />}
                    onClick={onDelete}
                    isDisabled={isDeleteDisabled}
                    size="xs"
                    variant="ghost"
                    color="red.500"
                    _hover={{ bg: 'red.50', color: 'red.600' }}
                    _active={{ bg: 'red.100' }}
                    _focus={{ boxShadow: 'none' }}
                />
            </Tooltip>
        )}
    </HStack>
);

export default SettingsRowActions;
